import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import {
    buildEncounterEntityMappingSchemaFromPaths,
    encounterEntityMappingBaseSchema,
    ENCOUNTER_NUMBER_PROPERTY,
    ensureEncounterNumberProperty,
    getEffectiveEncounterCreateProperties,
    getEffectiveEncounterUpdatePaths,
    getEncounterEntityMappingSelectedPaths,
    getEncounterEntityMappingSelectedProperties,
    getUnusedEncounterEntityMappingProperties,
    JsonSchemaRecord,
    validateEncounterEntityMappingSchema,
} from 'common/lib/model/documentScan/encounterEntityMappingSchema';
import { ENCOUNTER_FIELD_CATEGORIES, getEncounterFieldCategory } from './encounterFieldCategories';
import styles from './styles.module.scss';

interface EncounterMappingSchemaTreeProps {
    schema: JsonSchemaRecord;
    editable: boolean;
    createEnabled?: boolean;
    updateEnabled?: boolean;
    onChange: (schema: JsonSchemaRecord) => void;
    organizationSchema?: JsonSchemaRecord;
    columnDisabledReasons?: {
        create?: string;
        update?: string;
    };
}

interface EncounterFieldOption {
    key: string;
    title: string;
    description: string;
    category: string;
    isGroup: boolean;
    childCount: number;
}

interface EncounterChildFieldOption {
    key: string;
    title: string;
    description: string;
}

function isRecord(value: unknown): value is JsonSchemaRecord {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

const canonicalProperties: JsonSchemaRecord = isRecord(encounterEntityMappingBaseSchema.properties)
    ? (encounterEntityMappingBaseSchema.properties as JsonSchemaRecord)
    : {};

function getCanonicalChildren(fieldKey: string): JsonSchemaRecord | null {
    const property = canonicalProperties[fieldKey];
    if (!isRecord(property)) {
        return null;
    }
    if (isRecord(property.properties)) {
        return property.properties;
    }
    if (isRecord(property.items) && isRecord(property.items.properties)) {
        return property.items.properties;
    }
    return null;
}

function buildFieldOptions(): EncounterFieldOption[] {
    return Object.entries(canonicalProperties).map(([key, property]) => {
        const schema = isRecord(property) ? property : {};
        const children = getCanonicalChildren(key);
        return {
            key,
            title: typeof schema.title === 'string' ? schema.title : key,
            description: typeof schema.description === 'string' ? schema.description : '',
            category: getEncounterFieldCategory(key),
            isGroup: !!children,
            childCount: children ? Object.keys(children).length : 0,
        };
    });
}

function buildChildFieldOptions(fieldKey: string): EncounterChildFieldOption[] {
    const children = getCanonicalChildren(fieldKey);
    if (!children) {
        return [];
    }
    return Object.entries(children).map(([key, property]) => ({
        key,
        title: isRecord(property) && typeof property.title === 'string' ? property.title : key,
        description: isRecord(property) && typeof property.description === 'string' ? property.description : '',
    }));
}

function sameStringSet(left: Set<string>, right: Set<string>): boolean {
    if (left.size !== right.size) {
        return false;
    }
    return Array.from(left).every((value) => right.has(value));
}

function matchesSearch(search: string, ...values: string[]): boolean {
    if (!search) {
        return true;
    }
    return values.join(' ').toLowerCase().includes(search);
}

export function getEncounterSchemaComparisonSummary(
    schema: JsonSchemaRecord,
    organizationSchema: JsonSchemaRecord,
): { added: number; removed: number; settingChanges: number } {
    const normalized = ensureEncounterNumberProperty(schema);
    const normalizedOrganization = ensureEncounterNumberProperty(organizationSchema);
    const selectedPathSet = new Set(getEncounterEntityMappingSelectedPaths(normalized));
    const organizationPathSet = new Set(getEncounterEntityMappingSelectedPaths(normalizedOrganization));
    const createPathSet = new Set(getEffectiveEncounterCreateProperties(normalized));
    const organizationCreatePathSet = new Set(getEffectiveEncounterCreateProperties(normalizedOrganization));
    const updatePathSet = new Set(getEffectiveEncounterUpdatePaths(normalized));
    const organizationUpdatePathSet = new Set(getEffectiveEncounterUpdatePaths(normalizedOrganization));
    const comparablePaths = Array.from(selectedPathSet).filter((path) => path !== ENCOUNTER_NUMBER_PROPERTY);
    const comparableOrganizationPaths = Array.from(organizationPathSet).filter(
        (path) => path !== ENCOUNTER_NUMBER_PROPERTY,
    );

    return {
        added: comparablePaths.filter((path) => !organizationPathSet.has(path)).length,
        removed: comparableOrganizationPaths.filter((path) => !selectedPathSet.has(path)).length,
        settingChanges: Array.from(new Set([...Array.from(selectedPathSet), ...Array.from(organizationPathSet)])).filter(
            (path) =>
                path !== ENCOUNTER_NUMBER_PROPERTY &&
                selectedPathSet.has(path) &&
                organizationPathSet.has(path) &&
                (createPathSet.has(path) !== organizationCreatePathSet.has(path) ||
                    updatePathSet.has(path) !== organizationUpdatePathSet.has(path)),
        ).length,
    };
}

export default function EncounterMappingSchemaTree({
    schema,
    editable,
    createEnabled = true,
    updateEnabled = false,
    onChange,
    organizationSchema,
    columnDisabledReasons,
}: EncounterMappingSchemaTreeProps) {
    const normalizedSchema = useMemo(() => ensureEncounterNumberProperty(schema), [schema]);
    const fieldOptions = useMemo(buildFieldOptions, []);
    const fieldOptionsByKey = useMemo(
        () => new Map(fieldOptions.map((option) => [option.key, option])),
        [fieldOptions],
    );
    const selectedPaths = useMemo(() => getEncounterEntityMappingSelectedPaths(normalizedSchema), [normalizedSchema]);
    const selectedProperties = useMemo(
        () => getEncounterEntityMappingSelectedProperties(normalizedSchema),
        [normalizedSchema],
    );
    const updatePathSet = useMemo(
        () => new Set(getEffectiveEncounterUpdatePaths(normalizedSchema)),
        [normalizedSchema],
    );
    const createPathSet = useMemo(
        () => new Set(getEffectiveEncounterCreateProperties(normalizedSchema)),
        [normalizedSchema],
    );
    const unusedPaths = useMemo(
        () => new Set(getUnusedEncounterEntityMappingProperties(normalizedSchema)),
        [normalizedSchema],
    );
    const normalizedOrganizationSchema = useMemo(
        () => (organizationSchema ? ensureEncounterNumberProperty(organizationSchema) : undefined),
        [organizationSchema],
    );
    const organizationProperties = useMemo(
        () => new Set(getEncounterEntityMappingSelectedProperties(normalizedOrganizationSchema)),
        [normalizedOrganizationSchema],
    );
    const organizationPaths = useMemo(
        () =>
            normalizedOrganizationSchema
                ? new Set(getEncounterEntityMappingSelectedPaths(normalizedOrganizationSchema))
                : new Set<string>(),
        [normalizedOrganizationSchema],
    );
    const organizationUpdatePaths = useMemo(
        () =>
            normalizedOrganizationSchema
                ? new Set(getEffectiveEncounterUpdatePaths(normalizedOrganizationSchema))
                : new Set<string>(),
        [normalizedOrganizationSchema],
    );
    const organizationCreatePaths = useMemo(
        () =>
            normalizedOrganizationSchema
                ? new Set(getEffectiveEncounterCreateProperties(normalizedOrganizationSchema))
                : new Set<string>(),
        [normalizedOrganizationSchema],
    );

    const [fieldSearch, setFieldSearch] = useState('');
    const [addFieldModalOpen, setAddFieldModalOpen] = useState(false);
    const [modalExpandedGroups, setModalExpandedGroups] = useState<Set<string>>(() => new Set());
    const addFieldButtonRef = useRef<HTMLButtonElement>(null);
    const fieldModalRef = useRef<HTMLDivElement>(null);
    const fieldSearchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (addFieldModalOpen) {
            fieldSearchInputRef.current?.focus();
        }
    }, [addFieldModalOpen]);

    const selectedPropertySet = new Set(selectedProperties);
    const selectedPathSet = new Set(selectedPaths);
    const normalizedSearch = fieldSearch.trim().toLowerCase();
    const schemaValidation = validateEncounterEntityMappingSchema(normalizedSchema);
    const schemaUnsupported = !schemaValidation.valid;
    const listEditable = editable && !schemaUnsupported;
    const comparisonEnabled = !!normalizedOrganizationSchema;

    // Fields render in canonical schema order so rows keep a stable position while editing.
    const orderedSelectedFields = Object.keys(canonicalProperties)
        .filter((fieldKey) => selectedPropertySet.has(fieldKey))
        .map((fieldKey) => fieldOptionsByKey.get(fieldKey))
        .filter((option): option is EncounterFieldOption => !!option);

    const pathDiffersFromOrganization = (path: string) =>
        comparisonEnabled &&
        (selectedPathSet.has(path) !== organizationPaths.has(path) ||
            createPathSet.has(path) !== organizationCreatePaths.has(path) ||
            updatePathSet.has(path) !== organizationUpdatePaths.has(path));

    const currentCreatePaths = () =>
        Array.from(createPathSet).filter((path) => path !== ENCOUNTER_NUMBER_PROPERTY);
    const currentUpdatePaths = () => Array.from(updatePathSet);

    const updateSchema = (nextPaths: string[], nextCreatePaths: string[], nextUpdatePaths: string[]) => {
        onChange(
            buildEncounterEntityMappingSchemaFromPaths(nextPaths, normalizedSchema, {
                createFieldNames: nextCreatePaths,
                updateFieldNames: nextUpdatePaths,
            }),
        );
    };

    const addFieldPath = (path: string) => {
        updateSchema([...selectedPaths, path], [...currentCreatePaths(), path], currentUpdatePaths());
    };

    const removeFieldPath = (path: string) => {
        updateSchema(
            selectedPaths.filter((selectedPath) => selectedPath !== path),
            currentCreatePaths().filter((createPath) => createPath !== path),
            currentUpdatePaths().filter((updatePath) => updatePath !== path),
        );
    };

    const removeField = (fieldKey: string) => {
        const belongsToField = (path: string) => path === fieldKey || path.startsWith(`${fieldKey}.`);
        updateSchema(
            selectedPaths.filter((path) => !belongsToField(path)),
            currentCreatePaths().filter((path) => !belongsToField(path)),
            currentUpdatePaths().filter((path) => !belongsToField(path)),
        );
    };

    const toggleCreatePath = (path: string, selected: boolean) => {
        const nextCreatePaths = new Set(currentCreatePaths());
        if (selected) {
            nextCreatePaths.add(path);
        } else {
            nextCreatePaths.delete(path);
        }
        updateSchema(selectedPaths, Array.from(nextCreatePaths), currentUpdatePaths());
    };

    const toggleUpdatePath = (path: string, selected: boolean) => {
        const nextUpdatePaths = new Set(currentUpdatePaths());
        if (selected) {
            nextUpdatePaths.add(path);
        } else {
            nextUpdatePaths.delete(path);
        }
        updateSchema(selectedPaths, currentCreatePaths(), Array.from(nextUpdatePaths));
    };

    const toggleModalGroupExpanded = (fieldKey: string) => {
        setModalExpandedGroups((current) => {
            const next = new Set(current);
            if (next.has(fieldKey)) {
                next.delete(fieldKey);
            } else {
                next.add(fieldKey);
            }
            return next;
        });
    };

    const closeAddFieldModal = () => {
        setAddFieldModalOpen(false);
        setFieldSearch('');
        addFieldButtonRef.current?.focus();
    };

    const handleFieldModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeAddFieldModal();
            return;
        }
        if (event.key !== 'Tab' || !fieldModalRef.current) {
            return;
        }
        const focusableElements = Array.from(
            fieldModalRef.current.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
            ),
        );
        if (!focusableElements.length) {
            event.preventDefault();
            return;
        }
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    const createColumnActive = createEnabled && !columnDisabledReasons?.create;
    const updateColumnActive = updateEnabled && !columnDisabledReasons?.update;
    const createDisabledReason =
        columnDisabledReasons?.create || (!createEnabled ? 'Creation is off — see Creating encounters' : undefined);
    const updateDisabledReason =
        columnDisabledReasons?.update || (!updateEnabled ? 'Updates are off — see Existing encounters' : undefined);

    const renderCheckCell = (
        path: string,
        column: 'create' | 'update',
        checked: boolean,
        columnActive: boolean,
        inactiveReason: string | undefined,
        onToggle: (selected: boolean) => void,
        ariaLabel: string,
    ) => {
        const testId = `encounter-property-${column}-${path}`;
        if (!columnActive) {
            // Showing a checked-but-disabled box would imply the field is still applied,
            // so inactive columns render a quiet dash instead.
            return (
                <span
                    className={styles.propertyListInactive}
                    title={inactiveReason}
                    aria-label={`${ariaLabel}: not applicable`}
                    data-test-id={testId}
                    data-checked={checked ? 'true' : 'false'}
                >
                    —
                </span>
            );
        }
        if (!editable) {
            return (
                <span
                    className={classNames(styles.propertyListGlyph, {
                        [styles.propertyListGlyphChecked]: checked,
                    })}
                    aria-label={`${ariaLabel}: ${checked ? 'yes' : 'no'}`}
                    data-test-id={testId}
                    data-checked={checked ? 'true' : 'false'}
                >
                    {checked ? <Icon type="fa" name="check" aria-hidden="true" /> : '—'}
                </span>
            );
        }
        return (
            <input
                type="checkbox"
                checked={checked}
                disabled={!listEditable}
                aria-label={ariaLabel}
                onChange={(event) => onToggle(event.target.checked)}
                data-test-id={testId}
            />
        );
    };

    const renderRemoveButton = (label: string, onRemove: () => void, testId: string) => (
        <button
            type="button"
            className={styles.propertyListIconButton}
            onClick={onRemove}
            disabled={!listEditable}
            aria-label={label}
            title={label}
            data-test-id={testId}
        >
            <Icon type="fa" name="trash" aria-hidden="true" />
        </button>
    );

    const renderAddFieldModal = () => {
        const categorySections = ENCOUNTER_FIELD_CATEGORIES.map((category) => {
            const categoryFields = fieldOptions.filter((option) => option.category === category.key);
            const scalarFields = categoryFields.filter(
                (option) =>
                    !option.isGroup &&
                    option.key !== ENCOUNTER_NUMBER_PROPERTY &&
                    matchesSearch(normalizedSearch, option.title, option.key, option.description),
            );
            const groupSections = categoryFields
                .filter((option) => option.isGroup)
                .map((group) => {
                    const groupMatches = matchesSearch(normalizedSearch, group.title, group.key, group.description);
                    const childFields = buildChildFieldOptions(group.key).filter(
                        (child) =>
                            groupMatches ||
                            matchesSearch(normalizedSearch, child.title, child.key, child.description),
                    );
                    return { group, childFields, groupMatches };
                })
                .filter(({ childFields, groupMatches }) => groupMatches || childFields.length > 0);
            return { category, scalarFields, groupSections };
        }).filter(({ scalarFields, groupSections }) => scalarFields.length > 0 || groupSections.length > 0);

        const renderPickerRow = (
            path: string,
            title: string,
            code: string,
            description: string,
            isChild: boolean,
        ) => {
            const isAdded = selectedPathSet.has(path);
            return (
                <button
                    key={path}
                    type="button"
                    className={classNames(styles.fieldPickerRow, {
                        [styles.fieldPickerChildRow]: isChild,
                        [styles.fieldPickerRowAdded]: isAdded,
                    })}
                    disabled={isAdded}
                    onClick={isAdded ? undefined : () => addFieldPath(path)}
                    data-test-id={`encounter-field-add-${path}`}
                >
                    <span className={styles.fieldPickerRowText} title={description || undefined}>
                        <strong>{title}</strong>
                        <code>{code}</code>
                        {description ? <small>{description}</small> : null}
                    </span>
                    {isAdded ? (
                        <span className={styles.fieldPickerAdded}>
                            <Icon type="fa" name="check" aria-hidden="true" />
                        </span>
                    ) : (
                        <span className={styles.fieldPickerPlus}>
                            <Icon type="fa" name="plus" aria-hidden="true" />
                        </span>
                    )}
                </button>
            );
        };

        return (
            <div className={styles.fieldModalOverlay} onKeyDown={handleFieldModalKeyDown}>
                <div className={styles.fieldModalBackdrop} onClick={closeAddFieldModal} aria-hidden="true" />
                <div
                    ref={fieldModalRef}
                    className={styles.fieldModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="encounter-add-field-modal-title"
                    data-test-id="encounter-add-field-modal"
                >
                    <div className={styles.fieldModalHeader}>
                        <strong id="encounter-add-field-modal-title">Add encounter fields</strong>
                        <button
                            type="button"
                            onClick={closeAddFieldModal}
                            aria-label="Close"
                            data-test-id="encounter-add-field-close"
                        >
                            <Icon type="fa" name="times" aria-hidden="true" />
                        </button>
                    </div>
                    <input
                        ref={fieldSearchInputRef}
                        type="search"
                        className={styles.fieldPickerSearch}
                        value={fieldSearch}
                        placeholder="Search fields"
                        autoComplete="off"
                        onChange={(event) => setFieldSearch(event.target.value)}
                        data-test-id="encounter-property-search"
                    />
                    <div className={styles.fieldPickerList}>
                        {categorySections.length ? (
                            categorySections.map(({ category, scalarFields, groupSections }) => (
                                <React.Fragment key={category.key}>
                                    <span
                                        className={styles.propertyPickerCategory}
                                        data-test-id={`encounter-property-category-${category.key}`}
                                    >
                                        {category.label}
                                    </span>
                                    {scalarFields.map((option) =>
                                        renderPickerRow(
                                            option.key,
                                            option.title,
                                            option.key,
                                            option.description,
                                            false,
                                        ),
                                    )}
                                    {groupSections.map(({ group, childFields }) => {
                                        const isExpanded = !!normalizedSearch || modalExpandedGroups.has(group.key);
                                        return (
                                            <React.Fragment key={group.key}>
                                                <button
                                                    type="button"
                                                    className={styles.fieldPickerGroupHeader}
                                                    onClick={() => toggleModalGroupExpanded(group.key)}
                                                    aria-expanded={isExpanded}
                                                    data-test-id={`encounter-modal-group-toggle-${group.key}`}
                                                >
                                                    <Icon
                                                        type="fa"
                                                        name={isExpanded ? 'chevron-down' : 'chevron-right'}
                                                        aria-hidden="true"
                                                    />
                                                    <span>
                                                        <strong>{group.title}</strong>
                                                        <code>{group.key}</code>
                                                        <small>{group.childCount} fields</small>
                                                    </span>
                                                </button>
                                                {isExpanded
                                                    ? childFields.map((child) =>
                                                          renderPickerRow(
                                                              `${group.key}.${child.key}`,
                                                              child.title,
                                                              child.key,
                                                              child.description,
                                                              true,
                                                          ),
                                                      )
                                                    : null}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            ))
                        ) : (
                            <span className={styles.propertyPickerEmpty}>No fields match your search.</span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.propertyList} data-test-id="encounter-mapping-schema-tree">
            <div data-test-id="encounter-mapping-property-list">
                {schemaUnsupported ? (
                    <div className={styles.resultBanner} data-test-id="encounter-schema-blocked-error">
                        This saved schema contains unsupported or modified encounter field definitions and cannot be
                        edited in the field list.
                    </div>
                ) : null}

                <div className={styles.propertyListHeader}>
                    <span>Encounter field</span>
                    <span
                        title={createDisabledReason || 'Set when Capture AI creates a new encounter'}
                        data-test-id={
                            createDisabledReason
                                ? 'encounter-create-column-disabled-note'
                                : 'encounter-create-column-header'
                        }
                    >
                        New encounters
                    </span>
                    <span
                        title={updateDisabledReason || 'Updated when a scan is attached to an existing encounter'}
                        data-test-id={
                            updateDisabledReason
                                ? 'encounter-update-column-disabled-note'
                                : 'encounter-update-column-header'
                        }
                    >
                        Existing encounters
                    </span>
                    <span />
                </div>
                <ul className={styles.propertyListRows}>
                    {orderedSelectedFields.map((field) => {
                        const isEncounterNumber = field.key === ENCOUNTER_NUMBER_PROPERTY;
                        const selectedChildren = field.isGroup
                            ? buildChildFieldOptions(field.key).filter((child) =>
                                  selectedPathSet.has(`${field.key}.${child.key}`),
                              )
                            : [];
                        const isUnused = !field.isGroup && unusedPaths.has(field.key);
                        const selectedChildKeys = new Set(selectedChildren.map((child) => child.key));
                        const groupHasUpdates =
                            field.isGroup &&
                            selectedChildren.some((child) => updatePathSet.has(`${field.key}.${child.key}`));
                        const headerDiffersFromOrganization =
                            comparisonEnabled &&
                            (!organizationProperties.has(field.key) ||
                                (field.isGroup
                                    ? !sameStringSet(
                                          selectedChildKeys,
                                          new Set(
                                              Array.from(organizationPaths)
                                                  .filter((path) => path.startsWith(`${field.key}.`))
                                                  .map((path) => path.slice(field.key.length + 1)),
                                          ),
                                      )
                                    : pathDiffersFromOrganization(field.key)));
                        return (
                            <React.Fragment key={field.key}>
                                <li
                                    className={classNames(styles.propertyListRow, {
                                        [styles.propertyListRowFixed]: isEncounterNumber,
                                        [styles.propertyListGroupHeaderRow]: field.isGroup,
                                        [styles.propertyListRowChanged]: headerDiffersFromOrganization,
                                        [styles.propertyListRowUnused]: isUnused,
                                    })}
                                    data-test-id={`encounter-property-row-${field.key}`}
                                >
                                    <div className={styles.propertyListRowMain}>
                                        <span className={styles.propertyListIdentity} title={field.description || undefined}>
                                            <span className={styles.propertyListHeading}>
                                                <strong>{field.title}</strong>
                                                <code>{field.key}</code>
                                                {field.isGroup ? (
                                                    <small
                                                        className={styles.propertyListGroupCount}
                                                        data-test-id={`encounter-property-group-count-${field.key}`}
                                                    >
                                                        {selectedChildren.length} of {field.childCount} fields
                                                    </small>
                                                ) : null}
                                                {!field.isGroup && field.description ? (
                                                    <small className={styles.propertyListInlineDescription}>
                                                        {field.description}
                                                    </small>
                                                ) : null}
                                            </span>
                                            {field.isGroup && groupHasUpdates ? (
                                                <small className={styles.propertyListGroupCaption}>
                                                    Updating replaces the encounter&apos;s existing list.
                                                </small>
                                            ) : null}
                                            {isUnused ? (
                                                <small
                                                    className={styles.propertyListUnusedNote}
                                                    data-test-id={`encounter-property-unused-${field.key}`}
                                                >
                                                    Not used — check New or Existing, or remove this field.
                                                </small>
                                            ) : null}
                                        </span>
                                        <span className={styles.propertyListCheck}>
                                            {field.isGroup ? null : isEncounterNumber ? (
                                                createColumnActive ? (
                                                    <span
                                                        className={styles.propertyListLocked}
                                                        title="Always set — the encounter number identifies the encounter"
                                                        data-test-id="encounter-property-create-enctrNo"
                                                    >
                                                        <Icon type="fa" name="check" aria-hidden="true" />
                                                    </span>
                                                ) : (
                                                    <span
                                                        className={styles.propertyListInactive}
                                                        title={createDisabledReason}
                                                        data-test-id="encounter-property-create-enctrNo"
                                                    >
                                                        —
                                                    </span>
                                                )
                                            ) : (
                                                renderCheckCell(
                                                    field.key,
                                                    'create',
                                                    createPathSet.has(field.key),
                                                    createColumnActive,
                                                    createDisabledReason,
                                                    (selected) => toggleCreatePath(field.key, selected),
                                                    `Set ${field.title} on new encounters`,
                                                )
                                            )}
                                        </span>
                                        <span className={styles.propertyListCheck}>
                                            {field.isGroup ? null : isEncounterNumber ? (
                                                <span
                                                    className={styles.propertyListLocked}
                                                    title="Never updated — the encounter number is used to match encounters"
                                                    data-test-id={`encounter-property-update-unavailable-${field.key}`}
                                                >
                                                    <Icon type="fa" name="ban" aria-hidden="true" />
                                                </span>
                                            ) : (
                                                renderCheckCell(
                                                    field.key,
                                                    'update',
                                                    updatePathSet.has(field.key),
                                                    updateColumnActive,
                                                    updateDisabledReason,
                                                    (selected) => toggleUpdatePath(field.key, selected),
                                                    `Update ${field.title} on existing encounters`,
                                                )
                                            )}
                                        </span>
                                        <span className={styles.propertyListAction}>
                                            {isEncounterNumber ? (
                                                <span
                                                    className={styles.propertyListLocked}
                                                    title="This field is always included and can't be removed"
                                                    data-test-id="encounter-property-fixed-enctrNo"
                                                >
                                                    <Icon type="fa" name="lock" aria-hidden="true" />
                                                </span>
                                            ) : editable ? (
                                                renderRemoveButton(
                                                    `Remove ${field.title}`,
                                                    () => removeField(field.key),
                                                    `encounter-property-remove-${field.key}`,
                                                )
                                            ) : null}
                                        </span>
                                    </div>
                                </li>
                                {selectedChildren.map((child) => {
                                    const childPath = `${field.key}.${child.key}`;
                                    const childUnused = unusedPaths.has(childPath);
                                    return (
                                        <li
                                            key={childPath}
                                            className={classNames(styles.propertyListRow, styles.propertyListChildRow, {
                                                [styles.propertyListRowChanged]: pathDiffersFromOrganization(childPath),
                                                [styles.propertyListRowUnused]: childUnused,
                                            })}
                                            data-test-id={`encounter-property-child-row-${field.key}-${child.key}`}
                                        >
                                            <div className={styles.propertyListRowMain}>
                                                <span
                                                    className={classNames(
                                                        styles.propertyListIdentity,
                                                        styles.propertyListChildIdentity,
                                                    )}
                                                    title={child.description || undefined}
                                                >
                                                    <span className={styles.propertyListHeading}>
                                                        <strong>{child.title}</strong>
                                                        <code>{child.key}</code>
                                                        {child.description ? (
                                                            <small className={styles.propertyListInlineDescription}>
                                                                {child.description}
                                                            </small>
                                                        ) : null}
                                                    </span>
                                                    {childUnused ? (
                                                        <small
                                                            className={styles.propertyListUnusedNote}
                                                            data-test-id={`encounter-property-unused-${childPath}`}
                                                        >
                                                            Not used — check New or Existing, or remove this field.
                                                        </small>
                                                    ) : null}
                                                </span>
                                                <span className={styles.propertyListCheck}>
                                                    {renderCheckCell(
                                                        childPath,
                                                        'create',
                                                        createPathSet.has(childPath),
                                                        createColumnActive,
                                                        createDisabledReason,
                                                        (selected) => toggleCreatePath(childPath, selected),
                                                        `Set ${child.title} on new encounters`,
                                                    )}
                                                </span>
                                                <span className={styles.propertyListCheck}>
                                                    {renderCheckCell(
                                                        childPath,
                                                        'update',
                                                        updatePathSet.has(childPath),
                                                        updateColumnActive,
                                                        updateDisabledReason,
                                                        (selected) => toggleUpdatePath(childPath, selected),
                                                        `Update ${child.title} on existing encounters`,
                                                    )}
                                                </span>
                                                <span className={styles.propertyListAction}>
                                                    {editable
                                                        ? renderRemoveButton(
                                                              `Remove ${child.title}`,
                                                              () => removeFieldPath(childPath),
                                                              `encounter-property-child-remove-${field.key}-${child.key}`,
                                                          )
                                                        : null}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                    {listEditable ? (
                        <li className={styles.propertyListAddRow}>
                            <button
                                ref={addFieldButtonRef}
                                type="button"
                                onClick={() => setAddFieldModalOpen(true)}
                                data-test-id="encounter-add-field-button"
                            >
                                <Icon type="fa" name="plus" aria-hidden="true" />
                                Add field
                            </button>
                        </li>
                    ) : null}
                </ul>

                {listEditable && addFieldModalOpen ? renderAddFieldModal() : null}
            </div>
        </div>
    );
}
